import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { useCategoriesQuery } from "@/features/categories/use-categories";
import { flattenCategories } from "@/features/categories/types";
import { StockSerialsModal } from "./stock-serials-modal";

interface StockLevel {
  variantId: string;
  warehouseId: string;
  quantity: number;
  reserved: number;
  reorderPoint: number;
  sku: string | null;
  productName: string | null;
  warehouseName: string | null;
  updatedAt: string;
}

interface StockResponse {
  data: StockLevel[];
  meta: { total: number; limit: number; offset: number };
}

interface Warehouse {
  id: string;
  name: string;
}

// Dải thẻ tổng hợp đầu trang (GET /v1/inventory/summary).
interface StockSummary {
  products: number;
  variants: number;
  categories: number;
  units: number;
  costValue: number;
  sellValue: number;
}

function fmtInt(n?: number) {
  return n === undefined ? "…" : new Intl.NumberFormat("vi-VN").format(n);
}

function fmtMoney(n?: number) {
  return n === undefined ? "…" : new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

/** Debounce a rapidly-changing value so typing doesn't fire a request per keystroke. */
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Clickable column header: click to sort by this column, click again to flip.
function SortTh({
  col,
  sortBy,
  sortDir,
  onSort,
  align,
  children,
}: {
  col: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (c: string) => void;
  align?: "right";
  children: ReactNode;
}) {
  const active = sortBy === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <Th className={align === "right" ? "text-right" : ""}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""} hover:text-foreground ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}
      >
        {children}
        <Icon size={13} className={active ? "opacity-100" : "opacity-40"} />
      </button>
    </Th>
  );
}

export function StockListPage() {
  // Basic search
  const [q, setQ] = useState("");
  // Advanced filters
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [stockStatus, setStockStatus] = useState(""); // "" | "in" | "out"
  const [imei, setImei] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  // Mặc định: hàng CÒN TỒN nhiều lên đầu, hàng hết xuống cuối (Sếp yêu cầu 28/07).
  // Bấm header cột vẫn đổi được cách sắp như cũ.
  const [sortBy, setSortBy] = useState("quantity");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function toggleSort(col: string) {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setOffset(0);
  }

  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Stock row whose in-stock IMEIs are being viewed (click on the Tồn quantity).
  const [serialsFor, setSerialsFor] = useState<StockLevel | null>(null);

  // Debounce free-text inputs; dropdowns/checkbox apply immediately.
  const dq = useDebounced(q);
  const dImei = useDebounced(imei);
  const dMin = useDebounced(priceMin);
  const dMax = useDebounced(priceMax);

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      apiClient
        .get<{ data: Warehouse[] }>("/v1/warehouses")
        .then((r) => r.data.data),
  });

  const { data: catTree = [] } = useCategoriesQuery();
  const catOptions = flattenCategories(catTree);

  const hasAdvanced =
    categoryIds.length > 0 || !!stockStatus || !!dImei || !!dMin || !!dMax || !!warehouseId || lowOnly;

  // Reset to first page whenever any filter changes.
  useEffect(() => {
    setOffset(0);
  }, [dq, categoryIds, stockStatus, dImei, dMin, dMax, warehouseId, lowOnly]);

  const params: Record<string, string | number | boolean> = { limit, offset };
  if (dq) params.q = dq;
  if (warehouseId) params.warehouse_id = warehouseId;
  if (categoryIds.length) params.category_ids = categoryIds.join(",");
  if (stockStatus) params.stock_status = stockStatus;
  if (dImei) params.imei = dImei;
  if (dMin) params.price_min = dMin;
  if (dMax) params.price_max = dMax;
  if (lowOnly) params.low = "true";
  if (sortBy) { params.sort = sortBy; params.dir = sortDir; }

  const { data: result, isLoading } = useQuery({
    queryKey: ["stock", dq, warehouseId, categoryIds, stockStatus, dImei, dMin, dMax, lowOnly, sortBy, sortDir, offset],
    queryFn: () =>
      apiClient.get<StockResponse>("/v1/stock", { params }).then((r) => r.data),
  });

  const levels: StockLevel[] = result?.data ?? [];
  const total = result?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  // Dải thẻ tổng hợp đầu trang — bám theo kho đang lọc (yêu cầu Sếp 28/07).
  const { data: summary } = useQuery({
    queryKey: ["stock-summary", warehouseId],
    queryFn: () =>
      apiClient
        .get<{ data: StockSummary }>("/v1/inventory/summary", {
          params: warehouseId ? { warehouse_id: warehouseId } : {},
        })
        .then((r) => r.data.data),
  });

  function clearAll() {
    setQ("");
    setCategoryIds([]);
    setStockStatus("");
    setImei("");
    setPriceMin("");
    setPriceMax("");
    setWarehouseId("");
    setLowOnly(false);
  }

  function lowBadge(sl: StockLevel) {
    const isLow = sl.quantity <= sl.reorderPoint;
    return (
      <span
        className={
          isLow
            ? "rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300"
            : "rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-950/40 dark:text-green-300"
        }
      >
        {isLow ? "Sắp hết" : "OK"}
      </span>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Tồn kho</h1>

      {/* Tổng hợp nhanh — theo kho đang lọc (mặc định: mọi kho) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {[
          { label: "Sản phẩm còn tồn", value: fmtInt(summary?.products), sub: `${fmtInt(summary?.variants)} biến thể` },
          { label: "Nhóm hàng", value: fmtInt(summary?.categories), sub: "có hàng tồn" },
          { label: "Tổng số lượng", value: fmtInt(summary?.units), sub: "chiếc/máy" },
          { label: "Giá trị tồn (giá vốn)", value: fmtMoney(summary?.costValue), sub: "vốn đang nằm kho", accent: "text-amber-600" },
          { label: "Giá trị tồn (giá bán)", value: fmtMoney(summary?.sellValue), sub: "nếu bán hết", accent: "text-green-600" },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border bg-card p-3">
            <p className="text-xs text-muted-foreground">{c.label}</p>
            <p className={`text-lg font-bold tabular-nums ${c.accent ?? ""}`}>{c.value}</p>
            <p className="text-[11px] text-muted-foreground">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Basic search: product name or SKU */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1 max-w-md">
          <Search
            size={16}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm theo tên hoặc mã sản phẩm…"
            className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <Button
          variant={showAdvanced ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAdvanced((v) => !v)}
          className="gap-1.5"
        >
          <SlidersHorizontal size={15} />
          Tìm nâng cao
          {hasAdvanced && (
            <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-xs">•</span>
          )}
        </Button>
        {(q || hasAdvanced) && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1 text-muted-foreground">
            <X size={14} /> Xóa lọc
          </Button>
        )}
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1 text-sm">
            <span className="text-muted-foreground">Danh mục (chọn nhiều)</span>
            <MultiSelect
              values={categoryIds}
              onChange={setCategoryIds}
              options={catOptions.map((c) => ({ value: c.id, label: c.label }))}
              placeholder="Tất cả danh mục"
            />
          </div>

          <div className="space-y-1 text-sm">
            <span className="text-muted-foreground">Trạng thái tồn</span>
            <SearchableSelect
              value={stockStatus}
              onChange={setStockStatus}
              options={[
                { value: "in", label: "Còn tồn" },
                { value: "out", label: "Hết hàng" },
              ]}
              placeholder="Tất cả"
            />
          </div>

          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Kho</span>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="">Tất cả kho</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">IMEI / Serial</span>
            <input
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              placeholder="Nhập IMEI…"
              className="h-9 w-full rounded-md border border-input bg-background px-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </label>

          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Giá bán từ</span>
            <input
              type="number"
              min={0}
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              placeholder="0"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </label>

          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Giá bán đến</span>
            <input
              type="number"
              min={0}
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              placeholder="Không giới hạn"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </label>

          <label className="flex cursor-pointer select-none items-center gap-2 self-end pb-1.5 text-sm">
            <input
              type="checkbox"
              checked={lowOnly}
              onChange={(e) => setLowOnly(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Chỉ hàng sắp hết
          </label>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                {/* Tên · Kho · Tồn đứng đầu (Sếp yêu cầu 28/07 — mobile thấy ngay). */}
                <SortTh col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Tên sản phẩm</SortTh>
                <SortTh col="warehouse" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Kho</SortTh>
                <SortTh col="quantity" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right">Tồn</SortTh>
                <SortTh col="sku" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>SKU</SortTh>
                <SortTh col="reserved" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right">Đặt trước</SortTh>
                <SortTh col="reorder" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right">Điểm đặt hàng</SortTh>
                <SortTh col="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Trạng thái</SortTh>
              </Tr>
            </THead>
            <TBody>
              {levels.length === 0 && (
                <Tr>
                  <Td colSpan={7} className="text-center text-muted-foreground">
                    Không có dữ liệu
                  </Td>
                </Tr>
              )}
              {levels.map((sl) => (
                <Tr key={`${sl.variantId}-${sl.warehouseId}`}>
                  <Td className="min-w-[160px]">{sl.productName ?? "—"}</Td>
                  <Td>{sl.warehouseName ?? "—"}</Td>
                  <Td className="text-right font-semibold">
                    {sl.quantity > 0 ? (
                      <button
                        type="button"
                        onClick={() => setSerialsFor(sl)}
                        className="text-primary underline-offset-2 hover:underline"
                        title="Xem danh sách IMEI tồn kho"
                      >
                        {sl.quantity}
                      </button>
                    ) : (
                      sl.quantity
                    )}
                  </Td>
                  <Td className="font-mono text-xs">{sl.sku ?? "—"}</Td>
                  <Td className="text-right">{sl.reserved}</Td>
                  <Td className="text-right">{sl.reorderPoint}</Td>
                  <Td>{lowBadge(sl)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {total} bản ghi — trang {page}/{totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                >
                  Trước
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={page >= totalPages}
                >
                  Sau
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {serialsFor && (
        <StockSerialsModal
          variantId={serialsFor.variantId}
          warehouseId={serialsFor.warehouseId}
          productName={serialsFor.productName}
          sku={serialsFor.sku}
          warehouseName={serialsFor.warehouseName}
          onClose={() => setSerialsFor(null)}
        />
      )}
    </div>
  );
}
